// attachment — préparer une capture d'écran avant de l'envoyer au modèle.
//
// ── CE QUE ÇA DONNE À L'UTILISATEUR ─────────────────────────────────────────
//
// Montrer, au lieu de décrire. Demande de l'opérateur, le 2026-10-03 : un trombone pour
// joindre une image, et un second bouton qui capture l'app en un clic.
//
// ── LE COÛT EST LA CONTRAINTE, PAS UN DÉTAIL ────────────────────────────────
//
// « we should never forsake cost efficiency, it is a question of the platform survival. »
//
// Une image reste dans l'historique, et la documentation du fournisseur est explicite :
// « each request resends the full conversation history […] the full image bytes are included
// in the payload on every turn ». Une capture 4K non réduite coûterait 4 784 jetons
// visuels — à chaque tour, et nos runs font 33 tours en médiane, 98 au p90.
//
// Deux plafonds font donc tout le travail, et ils sont ici parce qu'ils doivent être tenus
// AVANT que l'image existe :
//
//   • 1024 px de plus grand côté → au pire 1 369 jetons, soit $0,0004 chez le modèle bon
//     marché. C'est le levier : réduire dans le navigateur ne coûte rien et borne tout.
//   • 2 images par message, refusées au-delà en le disant.
//
// ── POURQUOI GARDER L'IMAGE PLUTÔT QUE LA RETIRER PLUS TARD ─────────────────
//
// Mesuré contre la documentation du cache : les images SE METTENT en cache, mais « adding or
// removing images anywhere in the prompt » invalide tout le cache des messages. Retirer une
// image après un tour coûterait donc une invalidation complète (~$0,013 sur notre charge)
// contre $0,0008 pour la laisser. On la laisse — et c'est aussi le code le plus simple :
// l'historique n'est jamais réécrit.
//
// Tout ce fichier est PUR : platform-ui n'a pas de harnais de composants, donc une règle
// écrite dans un composant ne serait protégée par rien (voir chat_width.ts).

/** Le plus grand côté qu'on laisse partir. Au-delà, on réduit.
 *
 *  1024 et pas 1568 (le plafond du fournisseur) : à 1024 une capture d'interface reste
 *  parfaitement lisible — texte compris — et le pire cas tombe à 1 369 jetons au lieu de
 *  1 560. Le fournisseur réduirait de lui-même au-dessus de son plafond, mais il facturerait
 *  l'envoi ; réduire ici économise aussi le réseau. */
export const ATTACH_MAX_LONG_EDGE = 1024;

/** Combien d'images dans un message. Deux suffisent pour « avant / après », et bornent le
 *  coût d'un tour à moins de 3 000 jetons visuels. */
export const ATTACH_MAX_IMAGES = 2;

/** Les quatre types que le fournisseur sait lire, dans l'ordre de sa documentation.
 *  Liste FERMÉE : un `image/svg+xml` peut porter du script et n'a rien à faire dans un
 *  tuyau d'entrée, un `image/bmp` serait refusé par le fournisseur. */
export const ATTACH_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;

/** PURE. Ce type est-il acceptable ? Comparaison EXACTE, sans normalisation de casse : un
 *  navigateur rend un type de média en minuscules, et deviner au-delà ferait passer des
 *  variantes qu'on n'a pas éprouvées. */
export const acceptedImageType = (mime: string): boolean =>
  (ATTACH_TYPES as readonly string[]).includes(mime);

/** Un entier utilisable, ou 1. Une dimension absurde (0, négative, NaN, Infinity) ne doit
 *  pas devenir un canevas que le navigateur refuse de dessiner. */
const cote = (n: number): number =>
  Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;

/**
 * PURE. Les dimensions auxquelles réduire, en gardant les proportions.
 *
 * ON NE GRANDIT JAMAIS : agrandir coûterait des jetons pour des pixels inventés.
 * Le petit côté ne tombe jamais sous 1 px — une barre d'outils très allongée (4000×20)
 * donnerait sinon une hauteur de zéro, et le navigateur refuserait de dessiner.
 */
export function fittedSize(
  width: number,
  height: number,
  maxEdge: number = ATTACH_MAX_LONG_EDGE,
): { width: number; height: number } {
  const w = cote(width), h = cote(height);
  const grand = Math.max(w, h);
  if (grand <= maxEdge) return { width: w, height: h };
  const facteur = maxEdge / grand;
  return { width: Math.max(1, Math.round(w * facteur)), height: Math.max(1, Math.round(h * facteur)) };
}

/**
 * PURE. Ce que cette image coûtera, en jetons visuels.
 *
 * La FORMULE PUBLIÉE, pas une estimation : « Each patch is a 28×28-pixel block of the image,
 * referred to as a visual token. An image, therefore, costs ⌈width / 28⌉ × ⌈height / 28⌉
 * visual tokens. » Recoupée sur deux points de la table du fournisseur : 1000×1000 → 1296,
 * 1092×1092 → 1521.
 *
 * Elle n'est plus MONTRÉE (2026-10-18 : le client ne voit que son solde en dollars). Elle reste
 * la borne contre laquelle le plafond de côté est choisi — attachment.test.ts la tient.
 */
export function imageTokens(width: number, height: number): number {
  return Math.ceil(cote(width) / 28) * Math.ceil(cote(height) / 28);
}

export type AttachRefusal = "too-many" | "bad-type" | "unreadable" | "off";

/** Pourquoi la pièce jointe n'est pas partie, en mots utilisables.
 *
 *  AUCUN MESSAGE NE NOMME UN MODÈLE. La règle de la maison : ni l'interface ni un refus ne
 *  disent de quel modèle la plateforme se sert — le réglage lui-même s'appelle d'après ce
 *  qu'il fait, jamais d'après ce qu'il emploie. */
export function attachRefusal(reason: AttachRefusal): string {
  switch (reason) {
    case "too-many":
      return `Up to ${ATTACH_MAX_IMAGES} images per message. Remove one to add another.`;
    case "bad-type":
      return "That file isn't an image we can send. Use PNG, JPEG, WebP or GIF.";
    case "unreadable":
      return "That image could not be read. Try saving it again, or take a fresh screenshot.";
    case "off":
      return "Screenshots are part of the economy mode for your platform. Switch it on in settings to attach one.";
    default:
      return "That attachment could not be added. Try another image.";
  }
}

/**
 * PURE. Le type de média d'une adresse `data:`, ou `null`.
 *
 * POURQUOI ELLE EXISTE. Le navigateur re-encode l'image sur un canevas, et on lui demande
 * un format. S'il ne le connaît pas, ce qu'il rend n'est PAS documenté de façon que j'aie pu
 * vérifier (la page de référence n'a pas répondu, le 2026-10-03). Plutôt que de parier sur
 * un repli, on LIT le type qu'il a réellement produit et on le juge — un contrôle à
 * l'exécution vaut mieux qu'une hypothèse, et il reste juste si le navigateur change d'avis.
 */
export function dataUrlType(url: string): string | null {
  if (!url.startsWith("data:")) return null;
  const fin = url.indexOf(";base64,");
  if (fin <= "data:".length) return null;
  return url.slice("data:".length, fin);
}

/** Ce qu'un collage apporte, réduit à ce dont la décision dépend. Pas l'événement du
 *  navigateur : la lecture du DOM reste dans le composant, le jugement est ici et s'éprouve
 *  sans navigateur. `null` quand l'événement ne porte aucun presse-papiers. */
export type PasteContents = {
  /** `clipboardData.files` — un `FileList`, donc seulement `length` et des index. */
  readonly files: ArrayLike<File>;
  /** `clipboardData.getData("text/plain")`. */
  readonly text: string;
};

/**
 * PURE. Les fichiers à joindre depuis un collage, ou `null` pour laisser le champ de texte
 * faire son travail habituel.
 *
 * Demande de l'opérateur, le 2026-10-04 : « i want to be able to paste an image ».
 *
 * ── CE QU'ELLE NE JUGE PAS ──────────────────────────────────────────────────
 *
 * Ni le type, ni le nombre, ni la lisibilité. Tout ça est déjà jugé par `add`, donc par
 * `attachRefusal`, et doit le rester : deux juges finiraient par divergence, et le plafond
 * de coût aurait un trou de la taille d'un Ctrl-V. Elle tranche UNE question — prendre le
 * collage, ou le laisser passer — et transmet les fichiers tels quels.
 *
 * ── POURQUOI LE TEXTE GAGNE TOUJOURS ───────────────────────────────────────
 *
 * Un presse-papiers porte souvent les DEUX : une plage de cellules copiée depuis un tableur
 * y met le texte ET une image de la plage. Coller ça dans un champ de texte doit insérer le
 * texte, comme dans n'importe quel autre champ. On ne prend donc le collage que quand il n'y
 * a rien à insérer — et, délibérément, sans `trim` : coller des espaces est un collage de
 * texte légitime, et je n'ai aucune preuve qu'une copie d'image dépose des espaces dans
 * `text/plain`.
 *
 * ── CE SUR QUOI ELLE REPOSE, MESURÉ ─────────────────────────────────────────
 *
 * `@mdn/browser-compat-data` 8.1.4, lu le 2026-10-04 : `ClipboardEvent.clipboardData`
 * existe depuis Chrome 41, Edge 12, Firefox 22, Safari 10.1, Chrome Android 41, Safari iOS
 * 10.3, Firefox Android 22, Samsung 4.0 — partout, mobile compris. `DataTransfer.files` et
 * `getData` sont bien plus anciens encore (Chrome 3, Safari 4, iOS 3.2). `DataTransfer.items`
 * n'arrive qu'à Firefox 50 / Safari 11.1, donc on ne l'emploie pas : « we want a solution
 * that works for all users ».
 */
export function filesFromPaste(clip: PasteContents | null): readonly File[] | null {
  if (clip === null) return null;
  if (clip.text !== "") return null;
  // Un `FileList` n'est pas un tableau : il n'a que `length` et des index.
  return clip.files.length === 0 ? null : Array.from(clip.files);
}

